<template>
  <div class="w-full h-svh">
    <Providers>
      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>
    </Providers>
    <NewRequestAlertDialog
      :open="!!currentNotification"
      :request="currentNotification"
      @view-request="handleViewRequest"
      @dismiss="handleDismiss"
    />
    <EtaReminderDialog
      :open="!!currentEtaNotification"
      :case="currentEtaNotification"
      @view-case="handleViewCase"
      @dismiss="handleDismissEta"
    />
  </div>
</template>
<script lang="ts" setup>
import "@vuepic/vue-datepicker/dist/main.css";
import { onMounted } from "vue";
import { getAuthHeader } from "~/utils/network";

const { currentUser } = storeToRefs(useUserStore());

const { $posthog } = useNuxtApp();

watch(
  currentUser,
  (newVal, oldVal) => {
    const posthog = ($posthog as any)?.();
    if (newVal?.id !== oldVal?.id && posthog?.identify) {
      posthog.identify(newVal?.id, {
        email: newVal?.email,
        name: newVal?.displayName,
        userId: newVal?.id,
        deviceId: newVal?.deviceId,
      });
    }
  },
  { immediate: true, deep: true }
);

// New request notifications
const { currentNotification, isAdmin, startListening, dismissCurrent } = useRequestNotifications();

// ETA reminder notifications
const {
  currentNotification: currentEtaNotification,
  startListening: startEtaListening,
  dismissCurrent: dismissCurrentEta,
} = useEtaReminders();

// Start listening when user is logged in and is admin
watch(
  currentUser,
  (user) => {
    if (user && isAdmin.value) {
      startListening();
      startEtaListening();
    }
  },
  { immediate: true }
);

// Also start on mount if user is already logged in
onMounted(() => {
  if (currentUser.value && isAdmin.value) {
    startListening();
    startEtaListening();
  }
});

const handleViewRequest = (id: string) => {
  dismissCurrent();
};

const handleDismiss = () => {
  dismissCurrent();
};

const handleViewCase = async (id: string) => {
  // Acknowledge globally via API so all admins stop seeing this reminder
  try {
    await $fetch("/api/case/acknowledge-eta", {
      method: "POST",
      body: { caseId: id },
      headers: await getAuthHeader(),
    });
  } catch (error) {
    console.error("Error acknowledging ETA:", error);
  }
  dismissCurrentEta();
};

const handleDismissEta = async () => {
  const caseId = currentEtaNotification.value?.id;
  dismissCurrentEta();

  if (caseId) {
    try {
      await $fetch("/api/case/acknowledge-eta", {
        method: "POST",
        body: { caseId },
        headers: await getAuthHeader(),
      });
    } catch (error) {
      console.error("Error acknowledging ETA on dismiss:", error);
    }
  }
};
</script>
<style lang="css">
html {
  overflow: hidden;
}

.scroll-text {
  /* animation properties */
  -moz-transform: translateX(100%);
  -webkit-transform: translateX(100%);
  transform: translateX(100%);

  -moz-animation: my-animation 20s linear infinite;
  -webkit-animation: my-animation 20s linear infinite;
  animation: my-animation 20s linear infinite;
}

/* for Firefox */
@-moz-keyframes my-animation {
  from {
    -moz-transform: translateX(100%);
  }

  to {
    -moz-transform: translateX(-100%);
  }
}

/* for Chrome */
@-webkit-keyframes my-animation {
  from {
    -webkit-transform: translateX(100%);
  }

  to {
    -webkit-transform: translateX(-100%);
  }
}

@keyframes my-animation {
  from {
    -moz-transform: translateX(100%);
    -webkit-transform: translateX(100%);
    transform: translateX(100%);
  }

  to {
    -moz-transform: translateX(-100%);
    -webkit-transform: translateX(-100%);
    transform: translateX(-100%);
  }
}
</style>
